<template>
  <section class="message-thread">
    <header v-if="displayTitle" class="message-thread__header">
      <h3 class="message-thread__title">{{ displayTitle }}</h3>
    </header>

    <div v-if="loading && showLoadingStatus" class="message-thread__status">
      {{ loadingText }}
    </div>

    <div v-if="showSkeletonList" class="message-thread__skeleton" aria-hidden="true" aria-busy="true">
      <div v-for="i in skeletonRowCount" :key="'skeleton-' + i" class="message-thread__skeleton-item">
        <div class="mts-header">
          <div class="mts-avatar"></div>
          <div class="mts-meta">
            <div class="mts-line mts-line--name"></div>
            <div class="mts-line mts-line--time"></div>
          </div>
        </div>
        <div class="mts-body">
          <div class="mts-line mts-line--text mts-line--w80"></div>
        </div>
      </div>
    </div>

    <div v-else-if="displayThreadList.length > 0" class="message-thread__list">
      <MessageItem
        v-for="item in displayThreadList"
        :key="item.id"
        :message="item"
        :show-thread-children="!rootLevelOnly"
        :active-reply-message-id="activeReplyMessageId"
        :allow-reply="effectiveAllowReply"
        :allow-delete="effectiveAllowDelete"
        :max-depth="maxDepth"
        :reply-text="replyText"
        :cancel-reply-text="cancelReplyText"
        :delete-text="deleteText"
        :submit-reply-text="displaySubmitReplyText"
        :reply-placeholder="displayReplyPlaceholder"
        :current-user-avatar="currentUserAvatar"
        :current-user-name="currentUserName"
        :remark-reply-t-label="remarkTLabelReply"
        :remark-delete-t-label="remarkTLabelDeleteAction"
        :remark-reply-submit-t-label="remarkTLabelReply"
        :max-length="maxLength"
        :allow-line-break="allowLineBreak"
        :enable-leave-guard="effectiveLeaveGuard"
        :submitting="submitting"
        @reply="submitReplyMessage"
        @remark-draft-dirty="onRemarkDraftDirty"
        @delete="deleteMessage"
        @replying-change="handleReplyingChange"
      />
    </div>

    <MessageComposer
      v-if="effectiveAllowCreate && !hasActiveReplying"
      :placeholder="displayCreatePlaceholder"
      :submit-text="displayCreateButtonText"
      :submit-t-label="remarkTLabelCreateSubmit"
      :disabled="loading"
      :submitting="submitting"
      :avatar="currentUserAvatar"
      :avatar-name="currentUserName"
      :max-length="maxLength"
      :allow-line-break="allowLineBreak"
      :enable-leave-guard="effectiveLeaveGuard"
      @submit="submitRootMessage"
      @dirty-change="onRemarkDraftDirty"
    />
  </section>
</template>

<script>
import { messageBoardService } from "@/_services";
import { resolveLocalizedPersonName } from "@/_services/messageBoard.service";
import { LogService } from "@/_services/log.Service";
import MessageComposer from "./MessageComposer.vue";
import MessageItem from "./MessageItem.vue";

const SKELETON_DELAY_MS = 300;
const SKELETON_REVEAL_MIN_MS = 80;
const SUBMIT_SPINNER_MIN_MS = 600;
const MAX_SKELETON_ROWS = 15;
const MAX_ROW_COUNT_HINTS = 50;

/** 仅缓存各线索的备注行数（数字），不缓存留言内容 */
const rowCountHints = new Map();

function getRowCountHint(sourceKey) {
  if (!sourceKey) return 0;
  return rowCountHints.get(sourceKey) || 0;
}

function setRowCountHint(sourceKey, count) {
  if (!sourceKey || count <= 0) return;
  if (rowCountHints.size >= MAX_ROW_COUNT_HINTS && !rowCountHints.has(sourceKey)) {
    const firstKey = rowCountHints.keys().next().value;
    rowCountHints.delete(firstKey);
  }
  rowCountHints.set(sourceKey, count);
}

function waitFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveSkeletonHint(sourceKey, expectedSkeletonRows) {
  if (expectedSkeletonRows > 0) {
    return expectedSkeletonRows;
  }
  return getRowCountHint(sourceKey);
}

export default {
  name: "MessageThread",
  components: {
    MessageComposer,
    MessageItem
  },
  props: {
    title: {
      type: String,
      default: ""
    },
    sourceType: {
      type: String,
      default: ""
    },
    sourceId: {
      type: [String, Number],
      default: null
    },
    formId: {
      type: [String, Number],
      default: 0
    },
    messageType: {
      type: String,
      default: ""
    },
    maxDepth: {
      type: Number,
      default: 3
    },
    allowCreate: {
      type: Boolean,
      default: true
    },
    allowReply: {
      type: Boolean,
      default: true
    },
    allowDelete: {
      type: Boolean,
      default: false
    },
    /** 超级管理员模拟供应商时为 true，隐藏备注输入/回复/删除 */
    simulatedSupplier: {
      type: Boolean,
      default: false
    },
    /** 备注操作日志 FormType 前缀，如 SalesleadBack_ → SalesleadBack_Inquiry */
    logFormTypePrefix: {
      type: String,
      default: ""
    },
    /** 未保存备注时离开页面前提示；默认 SalesleadBack_ 前缀时开启 */
    enableLeaveGuard: {
      type: Boolean,
      default: null
    },
    /** 为 true 时才显示加载中文案；销售线索备注默认静默加载 */
    showLoadingStatus: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: "Loading messages..."
    },
    emptyText: {
      type: String,
      default: "No messages yet."
    },
    createPlaceholder: {
      type: String,
      default: ""
    },
    createButtonText: {
      type: String,
      default: ""
    },
    replyText: {
      type: String,
      default: "Reply"
    },
    cancelReplyText: {
      type: String,
      default: "Cancel"
    },
    deleteText: {
      type: String,
      default: "Delete"
    },
    submitReplyText: {
      type: String,
      default: ""
    },
    replyPlaceholder: {
      type: String,
      default: "Reply to this message..."
    },
    // When true, only top-level messages are shown; nested replies are not rendered.
    rootLevelOnly: {
      type: Boolean,
      default: false
    },
    /** 备注输入字数上限，0 表示不限制 */
    maxLength: {
      type: Number,
      default: 1500
    },
    /** 为 true 时允许换行；默认 false，输入时不保留回车符 */
    allowLineBreak: {
      type: Boolean,
      default: false
    },
    /** 可选：预知备注行数时用于骨架屏行数，0 表示自动推断 */
    expectedSkeletonRows: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      threadList: [],
      skeletonVisible: false,
      itemSkeletonVisible: false,
      skeletonRowCountResolved: 0,
      skeletonTimer: null,
      _loadStartedAt: 0,
      _activeSourceKey: "",
      currentUserAvatarFetched: "",
      currentUserNameFetched: "",
      // Only one inline reply composer open at a time (id of message being replied to).
      activeReplyMessageId: null
    };
  },
  computed: {
    resolvedSourceType() {
      return this.sourceType || "";
    },
    resolvedSourceId() {
      const sourceId = this.sourceId ?? this.formId;
      return Number(sourceId || 0);
    },
    resolvedMessageType() {
      return this.messageType || this.resolvedSourceType;
    },
    currentUser() {
      return this.$store?.state?.authentication?.user || {};
    },
    currentAccountUserId() {
      return Number(
        this.currentUser?.accountUserId ||
          this.currentUser?.AccountUserId ||
          this.currentUser?.accountUserID ||
          this.currentUser?.AccountUserID ||
          0
      );
    },
    currentUserAvatarFromStore() {
      const u = this.currentUser || {};
      const logoListItem = Array.isArray(u?.userLogoList) && u.userLogoList.length > 0 ? u.userLogoList[0] : null;
      return (
        u?.userLogo ||
        u?.UserLogo ||
        u?.userlogo ||
        u?.avatar ||
        u?.Avatar ||
        u?.photo ||
        u?.Photo ||
        u?.logo ||
        u?.Logo ||
        u?.logoFileName ||
        u?.LogoFileName ||
        logoListItem?.imageSrc ||
        logoListItem?.cdnLnk ||
        logoListItem?.fileName ||
        ""
      );
    },
    currentUserNameFromStore() {
      return (
        resolveLocalizedPersonName(this.currentUser, this.langId) ||
        this.currentUser?.loginUser ||
        ""
      );
    },
    currentUserMessageRecord() {
      const normalizeName = (v) => String(v || "").trim().toLowerCase();
      const expectedName = normalizeName(this.currentUserNameFromStore);
      const walk = (items = []) => {
        for (const item of items) {
          const itemName = normalizeName(resolveLocalizedPersonName(item));
          const hitById =
            this.currentAccountUserId > 0 &&
            Number(item?.accountUserId || 0) === this.currentAccountUserId;
          const hitByName = !!expectedName && itemName === expectedName;
          const createUserName = normalizeName(item?.createUser);
          const hitByCreateUser = !!expectedName && createUserName === expectedName;
          if (hitById || hitByName || hitByCreateUser) {
            return item;
          }
          const found = walk(item?.children || []);
          if (found) return found;
        }
        return null;
      };
      return walk(this.threadList);
    },
    currentUserAvatar() {
      const raw =
        this.currentUserAvatarFromStore ||
        this.currentUserAvatarFetched ||
        this.currentUserMessageRecord?.userLogo ||
        "";
      return this.normalizeAvatarUrl(raw);
    },
    currentUserName() {
      return (
        this.currentUserNameFromStore ||
        this.currentUserNameFetched ||
        resolveLocalizedPersonName(this.currentUserMessageRecord) ||
        ""
      );
    },
    hasActiveReplying() {
      return this.activeReplyMessageId != null && String(this.activeReplyMessageId).length > 0;
    },
    effectiveAllowCreate() {
      return this.allowCreate && !this.simulatedSupplier;
    },
    effectiveAllowReply() {
      return this.allowReply && !this.simulatedSupplier;
    },
    effectiveAllowDelete() {
      return this.allowDelete && !this.simulatedSupplier;
    },
    /** EMP_Salelead_{sourceType}_{sourceId}_ 供备注新增/删除/回复埋点 */
    remarkTLabelBase() {
      const st = String(this.resolvedSourceType || "Unknown").trim().replace(/\s+/g, "_");
      const sid = Number(this.resolvedSourceId || 0);
      return `EMP_Salelead_${st}_${sid}`;
    },
    remarkTLabelCreateSubmit() {
      return `${this.remarkTLabelBase}_备注新增`;
    },
    remarkTLabelReply() {
      return `${this.remarkTLabelBase}_备注回复`;
    },
    remarkTLabelDeleteAction() {
      return `${this.remarkTLabelBase}_备注删除`;
    },
    langId() {
      return localStorage.getItem("currentLangId") || "936";
    },
    /** 备注操作日志 FormType：{logFormTypePrefix}{sourceType} */
    remarkLogFormType() {
      const sourceType = String(this.resolvedSourceType || "").trim();
      if (!sourceType) {
        return "";
      }
      const prefix = String(this.logFormTypePrefix || "").trim();
      if (!prefix) {
        return sourceType;
      }
      const normalizedPrefix = prefix.endsWith("_") ? prefix : `${prefix}_`;
      if (sourceType.startsWith(normalizedPrefix)) {
        return sourceType;
      }
      return `${normalizedPrefix}${sourceType}`;
    },
    effectiveLeaveGuard() {
      if (this.enableLeaveGuard != null) {
        return !!this.enableLeaveGuard;
      }
      return String(this.logFormTypePrefix || "").startsWith("SalesleadBack_");
    },
    displayTitle() {
      return this.title || this.$t("lbl_EMPBE_SalesLead_Remarks_Title");
    },
    displayCreatePlaceholder() {
      return this.createPlaceholder || this.$t("lbl_EMPBE_SalesLead_Remarks_Placeholder");
    },
    displayCreateButtonText() {
      return this.createButtonText || this.$t("lbl_EMPBE_SalesLead_Remarks_Save");
    },
    displaySubmitReplyText() {
      return this.submitReplyText || this.$t("lbl_EMPBE_SalesLead_Remarks_Save");
    },
    displayReplyPlaceholder() {
      return this.replyPlaceholder || this.$t("lbl_EMPBE_SalesLead_Remarks_Placeholder");
    },
    displayThreadList() {
      if (!this.rootLevelOnly) {
        return this.threadList;
      }
      return this.flattenThreadList(this.threadList);
    },
    showSkeletonList() {
      return this.skeletonVisible || this.itemSkeletonVisible;
    },
    skeletonRowCount() {
      if (this.skeletonRowCountResolved > 0) {
        return Math.min(MAX_SKELETON_ROWS, this.skeletonRowCountResolved);
      }
      if (this.itemSkeletonVisible && this.threadList.length > 0) {
        return Math.min(MAX_SKELETON_ROWS, this.countDisplayRows(this.threadList));
      }
      if (this.skeletonVisible) {
        const hinted = resolveSkeletonHint(this._activeSourceKey, this.expectedSkeletonRows);
        return Math.min(MAX_SKELETON_ROWS, Math.max(1, hinted || 1));
      }
      return 0;
    }
  },
  watch: {
    sourceType: "handleRemarkSourceChange",
    sourceId: "handleRemarkSourceChange",
    formId: "handleRemarkSourceChange",
    messageType: "handleRemarkSourceChange",
    langId: "handleRemarkSourceChange"
  },
  created() {
    this.loadMessages();
    this.loadCurrentUserProfile();
  },
  beforeDestroy() {
    this.clearSkeletonTimer();
    if (this.effectiveLeaveGuard) {
      this.$store.state.dirty = false;
    }
  },
  methods: {
    clearSkeletonTimer() {
      if (this.skeletonTimer) {
        clearTimeout(this.skeletonTimer);
        this.skeletonTimer = null;
      }
      this.skeletonVisible = false;
      this.itemSkeletonVisible = false;
    },
    scheduleSkeleton(sourceKey) {
      this.clearSkeletonTimer();
      this.skeletonTimer = setTimeout(() => {
        if (!this.loading) return;
        if (this.displayThreadList.length > 0) {
          this.itemSkeletonVisible = true;
          return;
        }
        const hinted = resolveSkeletonHint(sourceKey, this.expectedSkeletonRows);
        if (hinted > 0) {
          this.skeletonVisible = true;
        }
      }, SKELETON_DELAY_MS);
    },
    async revealSkeletonBeforeContent(rowCount) {
      if (rowCount <= 0) return;
      this.clearSkeletonTimer();
      this.skeletonRowCountResolved = rowCount;
      this.skeletonVisible = true;
      await this.$nextTick();
      await waitFrame();
      await waitMs(SKELETON_REVEAL_MIN_MS);
    },
    handleRemarkSourceChange() {
      if (this.effectiveLeaveGuard) {
        this.$store.state.dirty = false;
      }
      this.loadMessages();
    },
    onRemarkDraftDirty(dirty) {
      if (!this.effectiveLeaveGuard) return;
      this.$store.state.dirty = !!dirty;
    },
    extractUserInfo(raw) {
      if (!raw || typeof raw !== "object") return null;
      const avatar =
        raw.userLogo ||
        raw.UserLogo ||
        raw.avatar ||
        raw.Avatar ||
        raw.logo ||
        raw.Logo ||
        raw.logoFileName ||
        raw.LogoFileName ||
        (Array.isArray(raw.userLogoList) && raw.userLogoList[0]
          ? raw.userLogoList[0].imageSrc || raw.userLogoList[0].cdnLnk || raw.userLogoList[0].fileName
          : "");
      const name =
        raw.personName ||
        raw.PersonName ||
        raw.personNameEn ||
        raw.PersonNameEn ||
        raw.personNameSc ||
        raw.PersonNameSc ||
        raw.displayName ||
        raw.DisplayName ||
        raw.userName ||
        raw.UserName ||
        raw.loginUser ||
        raw.LoginUser ||
        "";
      return {
        avatar: String(avatar || "").trim(),
        name: String(name || "").trim()
      };
    },
    loadCurrentUserProfile() {
      messageBoardService
        .getCurrentActor()
        .then((res) => {
          const payload = res?.data ?? res ?? null;
          const first = Array.isArray(payload) ? payload[0] : payload;
          const profile = this.extractUserInfo(first);
          if (!profile) return;
          if (profile.avatar) this.currentUserAvatarFetched = profile.avatar;
          if (profile.name) this.currentUserNameFetched = profile.name;
        })
        .catch(() => {
          // Ignore profile API failures and keep existing fallback chain.
        });
    },
    normalizeAvatarUrl(raw) {
      const value = String(raw || "").trim();
      if (!value) return "";
      if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
        return value;
      }
      const ossOrigin = String(process.env.VUE_APP_OSS_ORIGIN || "").trim();
      if (ossOrigin) {
        const base = ossOrigin.endsWith("/") ? ossOrigin : `${ossOrigin}/`;
        return `${base}${value.replace(/^\/+/, "")}`;
      }
      return `https://cdn.adsalecdn.com/${value.replace(/^\/+/, "")}`;
    },
    buildQuery() {
      const resolvedId = this.resolvedSourceId;
      return {
        sourceType: this.resolvedSourceType,
        sourceId: resolvedId,
        formId: resolvedId,
        type: this.resolvedMessageType,
        includeDeleted: false
      };
    },
    countDisplayRows(list) {
      return this.countTreeNodes(list);
    },
    countTreeNodes(items) {
      if (!Array.isArray(items) || !items.length) return 0;
      let total = 0;
      items.forEach((item) => {
        total += 1;
        if (item.children && item.children.length) {
          total += this.countTreeNodes(item.children);
        }
      });
      return total;
    },
    flattenThreadList(items) {
      const rows = [];
      (items || []).forEach((item) => {
        rows.push(item);
        if (item.children && item.children.length) {
          rows.push(...this.flattenThreadList(item.children));
        }
      });
      return rows;
    },
    loadMessages() {
      if (!this.resolvedSourceType || !this.resolvedSourceId) {
        this.threadList = [];
        this.activeReplyMessageId = null;
        this._activeSourceKey = "";
        this.loading = false;
        this.skeletonRowCountResolved = 0;
        this.clearSkeletonTimer();
        return;
      }

      this.loading = true;
      this._loadStartedAt = Date.now();
      const sourceKey = `${this.resolvedSourceType}:${this.resolvedSourceId}`;
      const hadExistingRows = this.displayThreadList.length > 0;
      if (sourceKey !== this._activeSourceKey) {
        this.threadList = [];
        this._activeSourceKey = sourceKey;
      }
      this.scheduleSkeleton(sourceKey);

      messageBoardService
        .listMessages(this.buildQuery())
        .then(async (res) => {
          const flat = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          const tree = messageBoardService.buildThreadTree(flat);
          const rowCount = this.countDisplayRows(tree);
          setRowCountHint(sourceKey, rowCount);

          const applyList = () => {
            this.threadList = tree;
            this.activeReplyMessageId = null;
            this.$emit("loaded", tree);
          };

          const loadElapsed = Date.now() - this._loadStartedAt;
          const isFirstLoad = !hadExistingRows && !this.itemSkeletonVisible;

          if (isFirstLoad && rowCount > 0 && loadElapsed >= SKELETON_DELAY_MS) {
            await this.revealSkeletonBeforeContent(rowCount);
          } else if ((this.skeletonVisible || this.itemSkeletonVisible) && rowCount > 0) {
            this.skeletonRowCountResolved = rowCount;
            await this.$nextTick();
            await waitFrame();
          }

          applyList();
        })
        .finally(() => {
          this.loading = false;
          this._loadStartedAt = 0;
          this.skeletonRowCountResolved = 0;
          this.clearSkeletonTimer();
        });
    },
    submitRootMessage(content) {
      const payload = {
        ...this.buildQuery(),
        parentId: 0,
        remark: content
      };
      this.submitMessage(payload);
    },
    submitReplyMessage({ parent, content }) {
      const payload = {
        ...this.buildQuery(),
        parentId: parent.id,
        rootId: parent.rootId || parent.id,
        level: Number(parent.level || 0) + 1,
        remark: content
      };
      this.submitMessage(payload);
    },
    logRemarkFormData(payload, action) {
      if (!this.resolvedSourceType || !this.resolvedSourceId) {
        return;
      }
      const formData = {
        formType: this.remarkLogFormType,
        sourceType: this.resolvedSourceType,
        sourceId: this.resolvedSourceId,
        messageId: payload.messageId ?? payload.id ?? null,
        parentId: payload.parentId ?? 0,
        rootId: payload.rootId ?? null,
        level: payload.level ?? null,
        remark: payload.remark ?? ""
      };
      LogService.LogFormData({
        FormId: this.resolvedSourceId,
        Action: action,
        FormType: this.remarkLogFormType,
        FormJSON: JSON.stringify(formData),
        ErrorLog: null
      });
    },
    submitMessage(payload) {
      this.submitting = true;
      const submitStartedAt = Date.now();
      messageBoardService
        .createMessage(payload)
        .then((res) => {
          const action = Number(payload.parentId || 0) > 0 ? "Reply" : "Submit";
          const createdId =
            res?.data?.id ??
            res?.data?.Id ??
            res?.id ??
            res?.Id ??
            null;
          this.logRemarkFormData(
            {
              ...payload,
              messageId: createdId
            },
            action
          );
          this.$emit("submitted", res);
          this.activeReplyMessageId = null;
          this.loadMessages();
        })
        .finally(() => {
          const remaining = SUBMIT_SPINNER_MIN_MS - (Date.now() - submitStartedAt);
          const finish = () => {
            this.submitting = false;
          };
          if (remaining > 0) {
            return waitMs(remaining).then(finish);
          }
          finish();
        });
    },
    deleteMessage(message) {
      const payload = {
        id: message.id,
        sourceType: this.resolvedSourceType,
        sourceId: this.resolvedSourceId
      };
      messageBoardService.removeMessage(payload).then((res) => {
        this.logRemarkFormData(
          {
            messageId: message.id,
            parentId: message.parentId,
            rootId: message.rootId,
            level: message.level,
            remark: message.remark
          },
          "Delete"
        );
        this.$emit("deleted", res);
        this.loadMessages();
      });
    },
    handleReplyingChange({ id, isReplying }) {
      const sid = id == null ? "" : String(id);
      if (!sid) return;
      if (isReplying) {
        this.activeReplyMessageId = id;
      } else if (String(this.activeReplyMessageId) === sid) {
        this.activeReplyMessageId = null;
      }
    }
  }
};
</script>

<style scoped>
.message-thread {
  display: grid;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #f2f3f5;
  border-radius: 24px;
  box-shadow: 0px 2px 15px rgba(183, 183, 183, 0.3);
  padding: 18px 20px;
}

.message-thread__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message-thread__title {
  margin: 0;
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 600;
}

.message-thread__status {
  border: 1px dashed #e1e1e1;
  border-radius: 8px;
  padding: 12px;
  color: #9ea5a8;
  font-size: 13px;
  text-align: center;
}

.message-thread__list {
  display: grid;
  gap: 16px;
  min-width: 0;
}

@keyframes mts-shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}

.message-thread__skeleton {
  display: grid;
  gap: 16px;
}

.message-thread__skeleton-item {
  display: grid;
  gap: 4px;
}

.mts-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.mts-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: mts-shimmer 1.4s infinite linear;
}

.mts-meta {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.mts-body {
  margin-left: 42px;
}

.mts-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: mts-shimmer 1.4s infinite linear;
}

.mts-line--name {
  flex: 1 1 auto;
  max-width: 45%;
  height: 14px;
}

.mts-line--time {
  flex: 0 0 auto;
  width: 96px;
  height: 12px;
}

.mts-line--text {
  width: 100%;
}

.mts-line--w80 {
  width: 80%;
}
</style>
